import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов амортизации
 */
const ComparisonSidebar = ({ 
  savedAssets, 
  deleteSavedAsset, 
  loadSavedAsset 
}) => {
  // Если нет сохраненных расчетов
  if (!savedAssets || savedAssets.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты амортизации для сравнения.
        </p>
      </div>
    );
  }

  // Функция для получения названия метода амортизации
  const getDepreciationMethodName = (method) => {
    const methodNames = {
      'straight_line': 'Линейный метод',
      'reducing_balance': 'Метод уменьшаемого остатка',
      'sum_of_years': 'По сумме чисел лет',
      'units_of_production': 'Пропорционально объему продукции'
    };
    return methodNames[method] || method;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedAssets.length} {savedAssets.length === 1 ? 'расчет' : savedAssets.length < 5 ? 'расчета' : 'расчетов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedAssets.map((asset) => (
          <div 
            key={asset.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {asset.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedAsset(asset.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedAsset(asset.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Стоимость:</span>
                <span className="font-medium">
                  {formatCurrency(asset.results.assetCost, 0)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Срок:</span>
                <span>
                  {asset.results.usefulLife} {pluralize(asset.results.usefulLife, 'год', 'года', 'лет')}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Метод:</span>
                <span>
                  {getDepreciationMethodShort(asset.method)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Ежегодно:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(asset.results.averageAnnualDepreciation, 0)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {savedAssets.length >= 2 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Сравнительный анализ
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left text-gray-700 dark:text-gray-300">
              <thead className="text-xs text-gray-600 dark:text-gray-400 uppercase">
                <tr>
                  <th className="px-1 py-1">Актив</th>
                  <th className="px-1 py-1 text-right">Год. амортизация</th>
                  <th className="px-1 py-1 text-right">Норма (%)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {savedAssets.map((asset) => (
                  <tr key={asset.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-1 py-1.5 whitespace-nowrap font-medium">{asset.name.length > 20 ? asset.name.substring(0, 20) + '...' : asset.name}</td>
                    <td className="px-1 py-1.5 whitespace-nowrap text-right">{formatCurrency(asset.results.averageAnnualDepreciation, 0)}</td>
                    <td className="px-1 py-1.5 whitespace-nowrap text-right">{formatNumber(asset.results.averageDepreciationRate, 1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Сокращенное название метода амортизации
const getDepreciationMethodShort = (method) => {
  const methodShortNames = {
    'straight_line': 'Линейный',
    'reducing_balance': 'Уменьш. остаток',
    'sum_of_years': 'Сумма лет',
    'units_of_production': 'Произв. метод'
  };
  return methodShortNames[method] || method;
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default ComparisonSidebar;